import React from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UnlockIncludes } from "@/components/UnlockIncludes";
import { useI18n } from "@/i18n";
import { PRICE_LABEL, LIST_PRICE_LABEL, DISCOUNT_LABEL } from "@/lib/pricing";
import { CreditCard, Loader2, Lock } from "lucide-react";

// Paywall contestuale, mostrato quando c'è già un referto pronto da generare. Dal 2026-08
// non è più la via principale al pagamento — quella è il muro a pieno schermo all'avvio di un
// nuovo screening (Paywall.jsx) — ma resta necessario per i percorsi in cui il credito finisce
// a compilazione iniziata: due schede sull'ultimo credito, revoca dell'admin a screening in
// corso, e il chip "Sblocca" in header (compreso il click sul logo).
export const UpgradeDialog = ({ open, onOpenChange, onPay, loading, patientName }) => {
  const { t } = useI18n();
  const body = patientName
    ? t("upg_body").replace("{patient}", patientName)
    : t("upg_body_np");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="upgrade-dialog">
        <DialogHeader>
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-2">
            <Lock className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <DialogTitle className="text-xl font-extrabold tracking-tight">{t("upg_title")}</DialogTitle>
          <DialogDescription className="text-base leading-relaxed text-slate-600">{body}</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
          <div className="flex items-end gap-2 flex-wrap">
            <span className="text-4xl font-extrabold tracking-tighter text-slate-900" data-testid="upgrade-dialog-price">{PRICE_LABEL}</span>
            {LIST_PRICE_LABEL && (
              <s className="mb-1 text-base font-semibold text-slate-400" data-testid="upgrade-dialog-list-price">{LIST_PRICE_LABEL}</s>
            )}
            {DISCOUNT_LABEL && (
              <span className="mb-1.5 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">{DISCOUNT_LABEL}</span>
            )}
            <span className="mb-1 text-sm font-medium text-slate-400">{t("lp_price_note")}</span>
          </div>
          <div className="mt-5">
            <UnlockIncludes items={[t("upg_f1"), t("upg_f2"), t("upg_f3"), t("upg_f4")]} />
          </div>
        </div>

        <DialogFooter className="sm:justify-start gap-2">
          <Button size="lg" onClick={onPay} disabled={loading} data-testid="upgrade-dialog-pay-btn"
            className="h-12 px-6 text-base font-semibold bg-primary hover:bg-primary/90 transition-colors">
            {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> {t("pw_redirect")}</>)
              : (<><CreditCard className="w-5 h-5" /> {t("upg_pay_btn")}</>)}
          </Button>
          {/* "Non adesso" non perde nulla: è ciò che separa un paywall accettabile da uno ostile. */}
          <Button size="lg" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}
            data-testid="upgrade-dialog-dismiss-btn" className="h-12 px-6 text-base font-semibold">
            {t("upg_not_now")}
          </Button>
        </DialogFooter>
        <p className="text-center text-xs text-slate-400">{t("pw_secure")}</p>
      </DialogContent>
    </Dialog>
  );
};
